import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';

export enum AnalyticsMessageType {
  ANALYTICS_EVENT = 'ANALYTICS_EVENT',
  ANALYTICS_EVENT_REPLY = 'ANALYTICS_EVENT_REPLY',
  ANALYTICS_ERROR = 'ANALYTICS_ERROR',
  ANALYTICS_STORE_EXIT_PAYLOAD = 'ANALYTICS_STORE_EXIT_PAYLOAD',
  ANALYTICS_STORE_EXIT_PAYLOAD_SUCCESS = 'ANALYTICS_STORE_EXIT_PAYLOAD_SUCCESS',
}

// Payload schemas without base message fields
const analyticsEventPayloadSchema = z.object({
  eventName: z.string(),
  eventParams: z.record(z.unknown()).optional(),
  userId: z.string().optional(),
  userProperties: z.record(z.unknown()).optional(),
  clientId: z.string().optional(),
  timestamp: z.number().optional(),
  ga4Fields: z.record(z.unknown()).optional(),
  tier1Filters: z.string().optional(),
  tier2Filters: z.string().optional(),
});

const analyticsEventReplyPayloadSchema = z.object({
  success: z.boolean(),
  eventId: z.string(),
  errorMessage: z.string().optional(),
});

const analyticsErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const analyticsStoreExitPayloadSchema = z.object({
  persistentId: z.string(),
  playerAuthId: z.string().optional(),
  playerConnectionId: z.string().optional(),
  broadcastSessionId: z.string().optional(),
  inviteId: z.string().optional(),
  sessionId: z.string().optional(), // Quickstart session ID
  flow: z.enum(['quickstart', 'prime', 'pavilion']).optional(),
  quickstartSlug: z.string().optional(),
  currentPhase: z.string().optional(), // Last phase player was in before exit
  sessionStartTime: z.number().optional(),
  pageLoadTime: z.number().optional(),
});

const analyticsStoreExitPayloadSuccessPayloadSchema = z.object({
  success: z.boolean(),
});

// Full message schemas including base fields
const analyticsEventSchema = baseMessageSchema
  .extend({
    type: z.literal(AnalyticsMessageType.ANALYTICS_EVENT),
  })
  .merge(analyticsEventPayloadSchema);

const analyticsEventReplySchema = baseMessageSchema
  .extend({
    type: z.literal(AnalyticsMessageType.ANALYTICS_EVENT_REPLY),
  })
  .merge(analyticsEventReplyPayloadSchema);

const analyticsErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(AnalyticsMessageType.ANALYTICS_ERROR),
  })
  .merge(analyticsErrorPayloadSchema);

const analyticsStoreExitPayloadMessageSchema = baseMessageSchema
  .extend({
    type: z.literal(AnalyticsMessageType.ANALYTICS_STORE_EXIT_PAYLOAD),
  })
  .merge(analyticsStoreExitPayloadSchema);

const analyticsStoreExitPayloadSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(AnalyticsMessageType.ANALYTICS_STORE_EXIT_PAYLOAD_SUCCESS),
  })
  .merge(analyticsStoreExitPayloadSuccessPayloadSchema);

export const analyticsMessageSchema = z.discriminatedUnion('type', [
  analyticsEventSchema,
  analyticsEventReplySchema,
  analyticsErrorSchema,
  analyticsStoreExitPayloadMessageSchema,
  analyticsStoreExitPayloadSuccessSchema,
]);

// Types for the payloads
export type AnalyticsEventPayload = z.infer<typeof analyticsEventPayloadSchema>;
export type AnalyticsEventReplyPayload = z.infer<typeof analyticsEventReplyPayloadSchema>;
export type AnalyticsErrorPayload = z.infer<typeof analyticsErrorPayloadSchema>;
export type AnalyticsStoreExitPayload = z.infer<typeof analyticsStoreExitPayloadSchema>;
export type AnalyticsStoreExitPayloadSuccess = z.infer<typeof analyticsStoreExitPayloadSuccessPayloadSchema>;

// Explicit types for full messages (for proper discriminated union support)
import type { BaseMessage } from './_base';

export type AnalyticsEvent = BaseMessage & {
  type: AnalyticsMessageType.ANALYTICS_EVENT;
} & AnalyticsEventPayload;

export type AnalyticsEventReply = BaseMessage & {
  type: AnalyticsMessageType.ANALYTICS_EVENT_REPLY;
} & AnalyticsEventReplyPayload;

export type AnalyticsError = BaseMessage & {
  type: AnalyticsMessageType.ANALYTICS_ERROR;
} & AnalyticsErrorPayload;

export type AnalyticsStoreExitPayloadMessage = BaseMessage & {
  type: AnalyticsMessageType.ANALYTICS_STORE_EXIT_PAYLOAD;
} & AnalyticsStoreExitPayload;

export type AnalyticsStoreExitPayloadSuccessMessage = BaseMessage & {
  type: AnalyticsMessageType.ANALYTICS_STORE_EXIT_PAYLOAD_SUCCESS;
} & AnalyticsStoreExitPayloadSuccess;

// Explicit discriminated union for proper exhaustive checking
export type AnalyticsMessage =
  | AnalyticsEvent
  | AnalyticsEventReply
  | AnalyticsError
  | AnalyticsStoreExitPayloadMessage
  | AnalyticsStoreExitPayloadSuccessMessage;

type PayloadType<T extends AnalyticsMessageType> = T extends AnalyticsMessageType.ANALYTICS_EVENT
  ? AnalyticsEventPayload
  : T extends AnalyticsMessageType.ANALYTICS_EVENT_REPLY
    ? AnalyticsEventReplyPayload
    : T extends AnalyticsMessageType.ANALYTICS_ERROR
      ? AnalyticsErrorPayload
      : T extends AnalyticsMessageType.ANALYTICS_STORE_EXIT_PAYLOAD
        ? AnalyticsStoreExitPayload
        : T extends AnalyticsMessageType.ANALYTICS_STORE_EXIT_PAYLOAD_SUCCESS
          ? AnalyticsStoreExitPayloadSuccess
          : never;

export function generateAnalyticsMessage<T extends AnalyticsMessageType>(type: T, payload: PayloadType<T>): AnalyticsMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
  };

  switch (type) {
    case AnalyticsMessageType.ANALYTICS_EVENT:
      return analyticsEventSchema.parse({
        ...base,
        ...payload,
        type: AnalyticsMessageType.ANALYTICS_EVENT,
      }) as AnalyticsEvent;
    case AnalyticsMessageType.ANALYTICS_EVENT_REPLY:
      return analyticsEventReplySchema.parse({
        ...base,
        ...payload,
        type: AnalyticsMessageType.ANALYTICS_EVENT_REPLY,
      }) as AnalyticsEventReply;
    case AnalyticsMessageType.ANALYTICS_ERROR:
      return analyticsErrorSchema.parse({
        ...base,
        ...payload,
        type: AnalyticsMessageType.ANALYTICS_ERROR,
      }) as AnalyticsError;
    case AnalyticsMessageType.ANALYTICS_STORE_EXIT_PAYLOAD:
      return analyticsStoreExitPayloadMessageSchema.parse({
        ...base,
        ...payload,
        type: AnalyticsMessageType.ANALYTICS_STORE_EXIT_PAYLOAD,
      }) as AnalyticsStoreExitPayloadMessage;
    case AnalyticsMessageType.ANALYTICS_STORE_EXIT_PAYLOAD_SUCCESS:
      return analyticsStoreExitPayloadSuccessSchema.parse({
        ...base,
        ...payload,
        type: AnalyticsMessageType.ANALYTICS_STORE_EXIT_PAYLOAD_SUCCESS,
      }) as AnalyticsStoreExitPayloadSuccessMessage;
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}

// Example usage:
// const analyticsEvent = generateAnalyticsMessage(AnalyticsMessageType.ANALYTICS_EVENT, {
//   eventName: 'button_click',
//   eventParams: {
//     buttonId: 'submit_button',
//     page: 'home'
//   },
//   userId: 'user123'
// });
